import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';

import dayjs, { Dayjs } from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { SectionCard, EmptyState, ConfirmDialog, currency, number } from './ui';
import { TRUE_COLORS } from '../theme';
import { withoutBusiness } from '../utils/businessHistory';

type Product = { id: number; name: string; price?: number };

// ---------- shared helpers ----------

const lifecycle = (item: { active: boolean; start_date: string; end_date: string }) => {
  const now = dayjs();
  if (!item.active) return { label: 'Pausada', bg: 'rgba(0,0,0,0.06)', color: TRUE_COLORS.secondary };
  if (now.isBefore(dayjs(item.start_date))) return { label: 'Programada', bg: 'rgba(111,137,148,0.16)', color: TRUE_COLORS.primary };
  if (now.isAfter(dayjs(item.end_date).endOf('day'))) return { label: 'Finalizada', bg: 'rgba(0,0,0,0.06)', color: TRUE_COLORS.secondary };
  return { label: 'Activa', bg: 'rgba(46,125,91,0.12)', color: TRUE_COLORS.success };
};

const LifecycleChip = ({ item }: { item: any }) => {
  const s = lifecycle(item);
  return <Chip size="small" label={s.label} sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600 }} />;
};

/** Human summary such as "15% de descuento en Coca-Cola" or "Regalo: Galletas". */
export const describePromo = (p: any) => {
  const target = p.over?.name ? ` en ${p.over.name}` : ' en toda la compra';
  const base = p.type === 'percentage'
    ? `${Math.round((Number(p.value) || 0) * 100)}% de descuento${target}`
    : p.type === 'amount'
      ? `${currency(p.value)} de descuento${target}`
      : `Regalo: ${p.over?.name ?? 'producto'}`;
  const conds = (p.conditions ?? []).length ? ` al comprar ${(p.conditions ?? []).map((c: any) => c.name).join(', ')}` : '';
  const min = Number(p.minimum) > 0 ? ` · mínimo ${currency(p.minimum)}` : '';
  return base + conds + min;
};

const dateRange = (start: string, end: string) => `${dayjs(start).format('D MMM')} – ${dayjs(end).format('D MMM YYYY')}`;

const apiError = (err: any, fallback: string) => {
  const d = err?.response?.data;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return Object.values(d).flat().join(' ').replace('Value must be between 0 and 1.', 'El porcentaje debe estar entre 0 y 100.');
  return fallback;
};

// ---------- promotion editor ----------

type PromoDraft = {
  id?: number; name: string; description: string; type: 'percentage' | 'amount' | 'free'; value: string;
  over: Product | null; conditions: Product[]; minimum: string; start: Dayjs; end: Dayjs; active: boolean; auto_apply: boolean;
  /** An automatic promo also shows in the menu and applies to online orders (servers before it: always). */
  online: boolean;
};

const newPromo = (): PromoDraft => ({
  name: '', description: '', type: 'percentage', value: '', over: null, conditions: [], minimum: '',
  start: dayjs().startOf('day'), end: dayjs().add(30, 'day').startOf('day'), active: true, auto_apply: true, online: true,
});

const fromPromo = (p: any): PromoDraft => ({
  id: p.id, name: p.name ?? '', description: p.description ?? '', type: p.type ?? 'percentage',
  value: p.type === 'percentage' ? String(Math.round((Number(p.value) || 0) * 10000) / 100) : String(p.value ?? ''),
  over: p.over ?? null, conditions: p.conditions ?? [], minimum: Number(p.minimum) ? String(p.minimum) : '',
  start: dayjs(p.start_date), end: dayjs(p.end_date), active: !!p.active, auto_apply: !!p.auto_apply, online: p.online !== false,
});

const PromoDialog = ({ draft, products, onClose, onSaved, onDelete }: {
  draft: PromoDraft | null; products: Product[]; onClose: () => void; onSaved: () => void; onDelete: (id: number) => void;
}) => {
  const axios = useAxios();
  const [d, setD] = React.useState<PromoDraft>(newPromo());
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { if (draft) { setD(draft); setError(''); } }, [draft]);
  const set = <K extends keyof PromoDraft>(k: K, v: PromoDraft[K]) => setD(x => ({ ...x, [k]: v }));

  const save = async () => {
    if (!d.name.trim()) { setError('Ponle un nombre a la promoción.'); return; }
    if (d.type !== 'free' && !(Number(d.value) > 0)) { setError('Indica el valor del descuento.'); return; }
    if (d.type === 'percentage' && Number(d.value) > 100) { setError('El porcentaje no puede ser mayor a 100.'); return; }
    if (d.type === 'free' && !d.over) { setError('Elige el producto de regalo.'); return; }
    if (d.end.isBefore(d.start)) { setError('La fecha de fin debe ser posterior al inicio.'); return; }
    const payload = {
      name: d.name.trim(), description: d.description.trim(), type: d.type,
      value: d.type === 'percentage' ? Number(d.value) / 100 : d.type === 'amount' ? Number(d.value) : 0,
      over: d.over ? { id: d.over.id } : null,
      conditions: d.conditions.map(c => ({ id: c.id })),
      minimum: Number(d.minimum) || 0,
      start_date: d.start.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      end_date: d.end.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      active: d.active, auto_apply: d.auto_apply, online: d.online,
    };
    setSaving(true);
    try {
      if (d.id) await axios.put(`api/discounts/${d.id}/`, payload);
      else await axios.post('api/discounts/', payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(apiError(err, 'No se pudo guardar la promoción.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!draft} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{d.id ? 'Editar promoción' : 'Nueva promoción'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Nombre" required autoFocus value={d.name} onChange={e => set('name', e.target.value)} placeholder="p. ej. 2x1 en refrescos" />
          <TextField label="Descripción" multiline minRows={2} value={d.description} onChange={e => set('description', e.target.value)} />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Tipo de promoción</Typography>
            <ToggleButtonGroup exclusive fullWidth size="small" value={d.type} onChange={(_, v) => v && setD(x => ({ ...x, type: v, value: '' }))}>
              <ToggleButton value="percentage">Porcentaje</ToggleButton>
              <ToggleButton value="amount">Monto fijo</ToggleButton>
              <ToggleButton value="free">Regalo</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Grid container spacing={2}>
            {d.type !== 'free' && (
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  label="Descuento"
                  type="number"
                  fullWidth
                  value={d.value}
                  onChange={e => set('value', e.target.value)}
                  slotProps={{
                    input: d.type === 'percentage'
                      ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                      : { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                    htmlInput: { min: 0, step: d.type === 'percentage' ? 1 : 0.01 },
                  }}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: d.type === 'free' ? 12 : 7 }}>
              <Autocomplete
                options={products}
                value={d.over}
                getOptionLabel={o => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, v) => set('over', v)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={d.type === 'free' ? 'Producto de regalo' : 'Aplica a'}
                    placeholder={d.type === 'free' ? 'Elige un producto' : 'Toda la compra'}
                    helperText={d.type === 'free' ? undefined : 'Déjalo vacío para descontar del total.'}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Autocomplete
            multiple
            options={products}
            value={d.conditions}
            getOptionLabel={o => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => set('conditions', v)}
            renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.id} />)}
            renderInput={params => <TextField {...params} label="Solo al comprar (opcional)" helperText="La promoción aplica si el ticket incluye estos productos." />}
          />

          <TextField
            label="Compra mínima (opcional)"
            type="number"
            value={d.minimum}
            onChange={e => set('minimum', e.target.value)}
            slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> }, htmlInput: { min: 0 } }}
          />

          <Grid container spacing={2}>
            <Grid size={6}><DatePicker label="Inicia" value={d.start} onChange={v => v && set('start', v)} slotProps={{ textField: { fullWidth: true } }} /></Grid>
            <Grid size={6}><DatePicker label="Termina" value={d.end} minDate={d.start} onChange={v => v && set('end', v)} slotProps={{ textField: { fullWidth: true } }} /></Grid>
          </Grid>

          <Stack spacing={1}>
            <FormControlLabel control={<Switch checked={d.active} onChange={e => set('active', e.target.checked)} />} label="Activa" />
            <FormControlLabel
              control={<Switch checked={d.auto_apply} onChange={e => set('auto_apply', e.target.checked)} />}
              label={
                <Box>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>Aplicar automáticamente en caja</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Si está apagado, el cajero la aplica manualmente o con un cupón de campaña.</Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', '& .MuiSwitch-root': { mt: -0.5 } }}
            />
            {d.auto_apply && (
              <FormControlLabel
                control={<Switch checked={d.online} onChange={e => set('online', e.target.checked)} inputProps={{ 'aria-label': 'También en el menú y los pedidos en línea' }} />}
                label={
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>También en el menú y los pedidos en línea</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Tus clientes la ven en tu menú digital y se descuenta sola en sus pedidos. Apágalo para dejarla solo en caja.</Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', '& .MuiSwitch-root': { mt: -0.5 } }}
              />
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {d.id && <Button color="error" onClick={() => onDelete(d.id!)} sx={{ mr: 'auto' }}>Eliminar</Button>}
        <Button color="inherit" onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{d.id ? 'Guardar cambios' : 'Crear promoción'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- campaign editor ----------

type CampaignDraft = { id?: number; name: string; description: string; discount: string; entries: string; start: Dayjs; end: Dayjs; active: boolean };

const CampaignDialog = ({ draft, promos, clientCount, onClose, onSaved, onDelete }: {
  draft: CampaignDraft | null; promos: any[]; clientCount: number; onClose: () => void; onSaved: () => void; onDelete: (id: number) => void;
}) => {
  const axios = useAxios();
  const [d, setD] = React.useState<CampaignDraft | null>(null);
  const [error, setError] = React.useState('');
  React.useEffect(() => { if (draft) { setD(draft); setError(''); } }, [draft]);
  if (!d) return null;
  const set = <K extends keyof CampaignDraft>(k: K, v: CampaignDraft[K]) => setD(x => x && ({ ...x, [k]: v }));

  const save = async () => {
    if (!d.name.trim()) { setError('Ponle un nombre a la campaña.'); return; }
    if (!d.id && !d.discount) { setError('Elige la promoción que vas a repartir.'); return; }
    if (!(Number(d.entries) > 0)) { setError('Indica cuántos cupones repartir.'); return; }
    const payload = {
      name: d.name.trim(), description: d.description.trim(), entries: Number(d.entries), active: d.active,
      start_date: d.start.startOf('day').format('YYYY-MM-DDTHH:mm:ss'), end_date: d.end.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      ...(d.id ? {} : { discount: Number(d.discount) }),
    };
    try {
      if (d.id) await axios.put(`api/campaigns/${d.id}/`, payload);
      else await axios.post('api/campaigns/', payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(apiError(err, 'No se pudo guardar la campaña.'));
    }
  };

  return (
    <Dialog open={!!draft} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{d.id ? 'Editar campaña' : 'Nueva campaña'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Nombre" required autoFocus value={d.name} onChange={e => set('name', e.target.value)} placeholder="p. ej. Clientes frecuentes de octubre" />
          <TextField label="Descripción" multiline minRows={2} value={d.description} onChange={e => set('description', e.target.value)} />
          <TextField select label="Promoción" required value={d.discount} onChange={e => set('discount', e.target.value)} disabled={!!d.id}
            helperText={d.id ? 'La promoción de una campaña no se puede cambiar.' : 'Los clientes recibirán un cupón para esta promoción.'}>
            {promos.length === 0 && <MenuItem disabled value="">Primero crea una promoción</MenuItem>}
            {promos.map(p => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
          </TextField>
          <TextField
            label="Cupones a repartir"
            type="number"
            required
            value={d.entries}
            onChange={e => set('entries', e.target.value)}
            helperText={`Tienes ${number(clientCount)} clientes registrados.`}
            slotProps={{ htmlInput: { min: 1 } }}
          />
          <Grid container spacing={2}>
            <Grid size={6}><DatePicker label="Inicia" value={d.start} onChange={v => v && set('start', v)} slotProps={{ textField: { fullWidth: true } }} /></Grid>
            <Grid size={6}><DatePicker label="Termina" value={d.end} minDate={d.start} onChange={v => v && set('end', v)} slotProps={{ textField: { fullWidth: true } }} /></Grid>
          </Grid>
          {d.id && <FormControlLabel control={<Switch checked={d.active} onChange={e => set('active', e.target.checked)} />} label="Activa" />}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {d.id && <Button color="error" onClick={() => onDelete(d.id!)} sx={{ mr: 'auto' }}>Eliminar</Button>}
        <Button color="inherit" onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>{d.id ? 'Guardar cambios' : 'Crear campaña'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- page ----------

const Promos = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tab = withoutBusiness(pathname).startsWith('/admin/campaigns') ? 'campaigns' : 'promos';

  const [promos, setPromos] = React.useState<any[]>([]);
  const [campaigns, setCampaigns] = React.useState<any[]>([]);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [clientCount, setClientCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [refresh, setRefresh] = React.useState(0);
  const [promoDraft, setPromoDraft] = React.useState<PromoDraft | null>(null);
  const [campaignDraft, setCampaignDraft] = React.useState<CampaignDraft | null>(null);
  const [toDelete, setToDelete] = React.useState<null | { kind: 'promo' | 'campaign'; id: number }>(null);

  React.useEffect(() => {
    (async () => {
      const [d, c, p, cl] = await Promise.allSettled([
        axios.get('api/discounts/'), axios.get('api/campaigns/'), axios.get('api/products/'), axios.get('api/clients/'),
      ]);
      if (d.status === 'fulfilled') setPromos(d.value.data ?? []);
      if (c.status === 'fulfilled') setCampaigns(c.value.data ?? []);
      if (p.status === 'fulfilled') setProducts(p.value.data ?? []);
      if (cl.status === 'fulfilled') setClientCount((cl.value.data ?? []).length);
      setLoading(false);
    })();
  }, [axios, refresh]);

  const toggle = async (kind: 'promo' | 'campaign', item: any, active: boolean) => {
    if (kind === 'promo') {
      await axios.put(`api/discounts/${item.id}/`, { ...item, active, over: item.over ? { id: item.over.id } : null, conditions: item.conditions ?? [] });
    } else {
      await axios.put(`api/campaigns/${item.id}/`, { ...item, active, discount: undefined });
    }
    setRefresh(r => r + 1);
  };

  const sortByStatus = (items: any[]) => [...items].sort((a, b) => {
    const order = ['Activa', 'Programada', 'Pausada', 'Finalizada'];
    return order.indexOf(lifecycle(a).label) - order.indexOf(lifecycle(b).label);
  });

  const newCampaign = (): CampaignDraft => ({
    name: '', description: '', discount: promos[0]?.id ? String(promos[0].id) : '', entries: String(clientCount || ''),
    start: dayjs().startOf('day'), end: dayjs().add(30, 'day'), active: true,
  });

  return (
    <PageShell
      title="Promociones"
      subtitle="Descuentos que TRUE POS aplica en caja y campañas de cupones para tus clientes"
      actions={
        tab === 'promos'
          ? <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPromoDraft(newPromo())}>Nueva promoción</Button>
          : <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCampaignDraft(newCampaign())}>Nueva campaña</Button>
      }
      toolbar={
        <Tabs value={tab} onChange={(_, v) => navigate(v === 'campaigns' ? '/admin/campaigns' : '/admin/promotions')}>
          <Tab value="promos" label={`Promociones (${promos.length})`} />
          <Tab value="campaigns" label={`Campañas (${campaigns.length})`} />
        </Tabs>
      }
    >
      {tab === 'promos' && (
        !loading && promos.length === 0 ? (
          <SectionCard>
            <EmptyState
              icon={<LocalOfferOutlinedIcon />}
              title="Crea tu primera promoción"
              description="Porcentajes, montos fijos o regalos, con fechas y condiciones. TRUE POS los aplica al cobrar."
              action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setPromoDraft(newPromo())}>Nueva promoción</Button>}
            />
          </SectionCard>
        ) : (
          <Grid container spacing={3}>
            {sortByStatus(promos).map(p => (
              <Grid key={p.id} size={{ xs: 12, md: 6, xl: 4 }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea onClick={() => setPromoDraft(fromPromo(p))} sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }} spacing={1}>
                      <Typography sx={{ fontWeight: 700 }}>{p.name}</Typography>
                      <LifecycleChip item={p} />
                    </Stack>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: TRUE_COLORS.primary }}>{describePromo(p)}</Typography>
                    {p.description && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{p.description}</Typography>}
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center', color: 'text.secondary' }}>
                      <EventOutlinedIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" sx={{ color: 'inherit' }}>{dateRange(p.start_date, p.end_date)}</Typography>
                      {p.auto_apply && <Chip size="small" variant="outlined" label="Automática" />}
                      {p.auto_apply && p.online !== false && <Chip size="small" variant="outlined" label="También en línea" />}
                    </Stack>
                  </CardActionArea>
                  <Stack direction="row" sx={{ px: 2.5, py: 1, borderTop: `1px solid ${TRUE_COLORS.borderSolid}`, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{p.active ? 'Encendida' : 'Apagada'}</Typography>
                    <Switch size="small" checked={!!p.active} onChange={e => toggle('promo', p, e.target.checked)} />
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      )}

      {tab === 'campaigns' && (
        !loading && campaigns.length === 0 ? (
          <SectionCard>
            <EmptyState
              icon={<CampaignOutlinedIcon />}
              title="Lanza una campaña"
              description="Reparte cupones de una promoción entre tus clientes registrados y mide cuántos regresan."
              action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCampaignDraft(newCampaign())} disabled={promos.length === 0}>Nueva campaña</Button>}
            />
            {promos.length === 0 && <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: -3, pb: 3 }}>Primero crea una promoción.</Typography>}
          </SectionCard>
        ) : (
          <Grid container spacing={3}>
            {sortByStatus(campaigns).map(c => (
              <Grid key={c.id} size={{ xs: 12, md: 6, xl: 4 }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea
                    onClick={() => setCampaignDraft({
                      id: c.id, name: c.name ?? '', description: c.description ?? '', discount: String(c.discount?.id ?? ''),
                      entries: String(c.entries ?? ''), start: dayjs(c.start_date), end: dayjs(c.end_date), active: !!c.active,
                    })}
                    sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }} spacing={1}>
                      <Typography sx={{ fontWeight: 700 }}>{c.name}</Typography>
                      <LifecycleChip item={c} />
                    </Stack>
                    {c.discount && <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{c.discount.name}: {describePromo(c.discount)}</Typography>}
                    {c.description && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{c.description}</Typography>}
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack direction="row" spacing={2} sx={{ mt: 2, color: 'text.secondary' }}>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <PeopleAltOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: 'inherit' }}>{number(c.entries)} cupones</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <EventOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: 'inherit' }}>{dateRange(c.start_date, c.end_date)}</Typography>
                      </Stack>
                    </Stack>
                  </CardActionArea>
                  <Stack direction="row" sx={{ px: 2.5, py: 1, borderTop: `1px solid ${TRUE_COLORS.borderSolid}`, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{c.active ? 'Encendida' : 'Apagada'}</Typography>
                    <Switch size="small" checked={!!c.active} onChange={e => toggle('campaign', c, e.target.checked)} />
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      )}

      <PromoDialog
        draft={promoDraft}
        products={products}
        onClose={() => setPromoDraft(null)}
        onSaved={() => setRefresh(r => r + 1)}
        onDelete={id => { setPromoDraft(null); setToDelete({ kind: 'promo', id }); }}
      />
      <CampaignDialog
        draft={campaignDraft}
        promos={promos}
        clientCount={clientCount}
        onClose={() => setCampaignDraft(null)}
        onSaved={() => setRefresh(r => r + 1)}
        onDelete={id => { setCampaignDraft(null); setToDelete({ kind: 'campaign', id }); }}
      />
      <ConfirmDialog
        open={!!toDelete}
        danger
        title={toDelete?.kind === 'promo' ? '¿Eliminar promoción?' : '¿Eliminar campaña?'}
        description={toDelete?.kind === 'promo' ? 'También se eliminarán las campañas que la usan.' : 'Los cupones repartidos dejarán de funcionar.'}
        confirmLabel="Eliminar"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await axios.delete(toDelete.kind === 'promo' ? `api/discounts/${toDelete.id}/` : `api/campaigns/${toDelete.id}/`);
          setToDelete(null);
          setRefresh(r => r + 1);
        }}
      />
    </PageShell>
  );
};

export default Promos;
